import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import ParticleBackground from '@/components/base/ParticleBackground';
import { newsCategories as categories, newsItems } from '@/pages/news/data';
const PAGE_SIZE = 3;

export default function News() {
  const [activeCategory, setActiveCategory] = useState<(typeof categories)[number]>('全部');
  const [activePage, setActivePage] = useState(1);

  const filteredNews = useMemo(
    () => (activeCategory === '全部' ? newsItems : newsItems.filter((item) => item.category === activeCategory)),
    [activeCategory],
  );

  const totalPages = Math.max(1, Math.ceil(filteredNews.length / PAGE_SIZE));

  const pagedNews = useMemo(() => {
    const start = (activePage - 1) * PAGE_SIZE;
    return filteredNews.slice(start, start + PAGE_SIZE);
  }, [activePage, filteredNews]);

  return (
    <div className="ui-page">
      <Navbar />
      <main className="pt-20">
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background-900/40 to-transparent z-[1]"></div>
          <ParticleBackground compact />
          <div className="w-full px-4 md:px-8 lg:px-12 relative z-10">
            <div className="max-w-screen-2xl mx-auto">
              <ScrollReveal>
                <div className="text-center mb-16">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
                    <span className="w-1 h-1 rounded-full bg-primary-500"></span>
                    <span className="text-primary-300 text-xs font-medium tracking-wider whitespace-nowrap">News</span>
                  </div>
                  <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-primary-500 mb-6">新闻动态</h1>
                   <p className="text-secondary-400 text-lg max-w-2xl mx-auto">企业新闻、项目动态、技术成果、合作签约与联盟动态持续更新</p>
                </div>
              </ScrollReveal>

              <div className="flex flex-wrap justify-center gap-3 mb-12">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => {
                      setActiveCategory(cat);
                      setActivePage(1);
                    }}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                      activeCategory === cat
                        ? 'bg-primary-500 text-background-950 shadow-[0_0_12px_rgba(180,140,80,0.28)]'
                        : 'bg-background-800 text-secondary-400 hover:text-foreground-50 hover:bg-background-700 hover:shadow-[0_0_10px_rgba(180,140,80,0.1)]'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="space-y-6">
                {pagedNews.map((item) => (
                  <ScrollReveal key={item.id}>
                    <div className="group bg-background-900/50 rounded-2xl border border-primary-500/10 hover:border-primary-500/25 transition-all duration-500 overflow-hidden flex flex-col md:flex-row hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(180,140,80,0.1)]">
                      <div className="md:w-64 lg:w-80 h-48 md:h-auto flex-shrink-0 overflow-hidden">
                        <img src={item.cover} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      </div>
                      <div className="flex-1 p-6 md:p-8 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-3 py-1 rounded-full bg-accent-500/15 border border-accent-500/20 text-accent-300 text-xs font-medium whitespace-nowrap group-hover:bg-accent-500/25 transition-all duration-500">{item.category}</span>
                          {item.tag && (
                            <span className="px-2 py-1 rounded-full bg-primary-500/15 border border-primary-500/20 text-primary-400 text-xs font-medium whitespace-nowrap">{item.tag}</span>
                          )}
                          <span className="text-secondary-500 text-xs">{item.date}</span>
                        </div>
                        <h3 className="font-heading text-xl font-semibold text-foreground-50 mb-3 group-hover:text-primary-500 transition-colors duration-500">{item.title}</h3>
                        <p className="text-secondary-400 text-sm leading-relaxed">{item.excerpt}</p>
                        <Link
                          to={`/news/${item.id}`}
                          className="inline-flex items-center gap-1 mt-4 text-primary-500 text-xs font-medium whitespace-nowrap opacity-0 group-hover:opacity-100 group-hover:gap-2 transition-all duration-300"
                        >
                          阅读全文
                          <div className="w-3 h-3 flex items-center justify-center">
                            <i className="ri-arrow-right-line"></i>
                          </div>
                        </Link>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary-500/0 to-transparent group-hover:via-primary-500/40 transition-all duration-500 hidden md:block"></div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>

              <div className="flex justify-center mt-12 gap-2">
                {Array.from({ length: totalPages }, (_unused, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => setActivePage(page)}
                    className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-300 cursor-pointer ${
                      page === activePage
                        ? 'bg-primary-500 text-background-950 shadow-[0_0_10px_rgba(180,140,80,0.3)]'
                        : 'bg-background-800 text-secondary-400 hover:text-foreground-50 hover:bg-background-700 hover:shadow-[0_0_10px_rgba(180,140,80,0.1)]'
                    }`}
                  >
                    {page}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
